import React, { useImperativeHandle, forwardRef, useState, useCallback, useRef, useEffect } from 'react';
import { Form, Drawer, Input, Radio, Select, message, Button, Space, Spin, Typography } from 'antd';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { CategorySelect } from './CategorySelect';
import { addProduct, getProductInfo, getDeviceServers } from '@/api';
import { StandardFuncModal, StandardFuncModalRef } from './StandardFuncModal';
import { AddProductParams, putProduct } from '@/api/product';
import { DeviceServers } from '@/api/device-servers';
import { ReactComponent as DirectDeviceIcon } from '@/assets/images/直连设备48.svg';
import { ReactComponent as GatewayDeviceIcon } from '@/assets/images/网关设备48.svg';
import { ReactComponent as GatewaySubDeviceIcon } from '@/assets/images/网关子设备48.svg';
import { ReactComponent as CameraIcon } from '@/assets/images/摄像头48.svg';
import './NodeTypeSelector.less';

const { useForm } = Form;
const { TextArea } = Input;
const { Link } = Typography;

// 生成20位随机编码（包含大小写字母）
const generateProductCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let code = '';
  for (let i = 0; i < 15; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

// 节点类型配置
const nodeTypes = [
  { label: '直连设备', value: '直连设备', icon: DirectDeviceIcon },
  { label: '网关设备', value: '网关设备', icon: GatewayDeviceIcon },
  { label: '网关子设备', value: '网关子设备', icon: GatewaySubDeviceIcon },
  { label: '摄像头', value: '摄像头', icon: CameraIcon },
];

export interface AddModalProps {
  onFinish: () => void;
}

export interface AddModalRef {
  open: (record?: any) => void;
}

export const AddModal = forwardRef<AddModalRef, AddModalProps>(({ onFinish }, ref) => {
  const [form] = useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const standardFuncModalRef = useRef<StandardFuncModalRef>(null);
  const categoryKeyRef = useRef('');
  const [loading, setLoading] = useState<boolean>(false);

  const getProductData = async (productId: string) => {
    setLoading(true);
    try {
      const res = await getProductInfo(productId);
      const result = res.result as AddProductParams & { category_template_id2?: string };
      if (result?.category_template_id) {
        result.category_template_id2 = result.category_template_id;
        result.category_template_id = '0';
      } else {
        result.category_template_id = '1';
      }

      form.setFieldsValue(result || {});
    } catch {
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(
    ref,
    () => {
      return {
        open(record) {
          if (record?.id) {
            getProductData(record.id);
          } else {
            // 新建时自动生成产品编码，并设置其他默认值
            const defaultValues: any = { key: generateProductCode() };

            // 如果传入了 category_template_id 和 category_template_name，设置为默认值
            if (record?.category_template_id) {
              // 如果有 category_template_id，自动选择标准品类
              defaultValues.category_template_id2 = record.category_template_id;
              defaultValues.category_template_id = '0';
              
              // 如果有 category_template_name，保存到 localStorage，以便 CategorySelect 立即显示
              if (record?.category_template_name) {
                const CATEGORY_STORAGE_KEY = 'product_category_cache';
                try {
                  const stored = localStorage.getItem(CATEGORY_STORAGE_KEY);
                  const categories = stored ? JSON.parse(stored) : {};
                  categories[record.category_template_id] = {
                    id: record.category_template_id,
                    name: record.category_template_name,
                  };
                  localStorage.setItem(CATEGORY_STORAGE_KEY, JSON.stringify(categories));
                } catch (error) {
                  console.error('保存品类信息到 localStorage 失败:', error);
                }
              }
            } else {
              // 如果 category_template_id 为空，默认选择自定义品类
              defaultValues.category_template_id = '1';
            }
            
            if (record?.name) {
              defaultValues.name = record.name;
            }

            form.setFieldsValue(defaultValues);
          }
          setIsModalOpen(true);
        },
      };
    },
    [form],
  );

  const handleClose = useCallback(() => {
    form.resetFields();
    setIsModalOpen(false);
  }, [form]);

  const [protocolList, setProtocolList] = useState<{ label: string; value: string }[]>([]);
  const getProtocolList = async () => {
    try {
      const { result } = await getDeviceServers({ page: 1, pageSize: 1000 });
      if (result?.list) {
        setProtocolList(result.list.map((item: DeviceServers) => ({ value: item.id, label: item.name })));
      }
    } catch {}
  };
  useEffect(() => {
    getProtocolList();
  }, []);

  return (
    <Drawer
      title="创建产品"
      placement="right"
      open={isModalOpen}
      width={550}
      closable={false}
      maskClosable={false}
      styles={{ header: { height: 51 } }}
      destroyOnHidden={false}
      extra={<CloseOutlined onClick={handleClose} />}
      onClose={handleClose}
      footer={
        <Space>
          <Button
            type="primary"
            loading={submitting}
            disabled={submitting}
            onClick={() => {
              form.validateFields().then((values) => {
                if ('category_template_id2' in values) {
                  values.category_template_id = values.category_template_id2;
                  delete values.category_template_id2;
                } else {
                  delete values.category_template_id;
                }

                setSubmitting(true);
                (values?.id ? putProduct : addProduct)(values)
                  .then((resp) => {
                    if (resp.success) {
                      message.success(`${values?.id ? '编辑' : '添加'}成功`);
                      handleClose();
                      onFinish();
                    } else {
                      message.error(resp.errorMsg);
                    }
                  })
                  .finally(() => {
                    setSubmitting(false);
                  });
              });
            }}
          >
            确定
          </Button>
          <Button onClick={handleClose}>取消</Button>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <StandardFuncModal ref={standardFuncModalRef} />
        <Form form={form} layout="vertical">
          <Form.Item label="产品名称" name="name" rules={[{ required: true, message: '请输入产品名称' }]}>
            <Input placeholder="请输入产品名称"></Input>
          </Form.Item>
          <Form.Item label="产品编码" name="key" rules={[{ required: true, message: '请输入产品编码' }]}>
            <Input placeholder="请输入产品编码"></Input>
          </Form.Item>
          {/* <Form.Item
          label="云平台"
          name="cloud_platform"
          rules={[{ required: true, message: '请选择云平台' }]}
        >
          <Select
            placeholder="请输入产品名称"
            options={[{ label: '本地', value: '本地' }]}
          ></Select>
        </Form.Item> */}
          <Form.Item noStyle name="id">
            <div></div>
          </Form.Item>
          <Form.Item label="所属品类" name="category_template_id" initialValue="1" extra="选择标准品类时, 需从系统生成的品类列表中选择一个品类" rules={[{ required: true, message: '请选择所属品类' }]}>
            <Radio.Group>
              <Radio value="1">自定义品类</Radio>
              <Radio value="0">标准品类</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, nextValues) => {
              return prevValues.category_template_id !== nextValues.category_template_id || prevValues.category_template_id2 !== nextValues.category_template_id2;
            }}
          >
            {({ getFieldValue }) => {
              const category_template_id2 = getFieldValue('category_template_id2');
              return getFieldValue('category_template_id') === '0' ? (
                <Form.Item
                  label="选择标准品类"
                  name="category_template_id2"
                  initialValue={undefined}
                  rules={[{ required: true, message: '请选择标准品类' }]}
                  extra={
                    category_template_id2 && (
                      <div className="mt10">
                        {/* <Typography.Link
                          onClick={() => {
                            standardFuncModalRef.current?.open(categoryKeyRef.current);
                          }}
                        >
                          查看功能
                        </Typography.Link> */}
                      </div>
                    )
                  }
                >
                  <CategorySelect
                    onCategoryKeyChange={(category_key) => {
                      // console.log(category_key);
                      categoryKeyRef.current = category_key;
                    }}
                  />
                  {/* <DebounceSelect
                    placeholder="请选择品类"
                    fetchOptions={async (category_name: string) => {
                      try {
                        const resp = await categoryTemplate<CategoryTemplate>({ category_name })
                        return resp.success
                          ? resp.result.list.map(item => {
                            return {
                              label: (
                                <div>
                                  <div>{item.category_name}</div>
                                  <div>{item.scene}</div>
                                </div>
                              ),
                              value: item.id,
                            }
                          })
                          : []
                      } catch (error) {
                        return []
                      }
                    }}
                  /> */}
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
          <Form.Item label="节点类型" name="node_type" rules={[{ required: true, message: '请选择节点类型' }]} initialValue={'直连设备'}>
            <Form.Item noStyle shouldUpdate>
              {({ getFieldValue, setFieldsValue }) => {
                const selectedType = getFieldValue('node_type');
                return (
                  <div className="node-type-selector">
                    {nodeTypes.map((type) => {
                      const IconComponent = type.icon;
                      return (
                        <div key={type.value} className={`node-type-card ${selectedType === type.value ? 'selected' : ''}`} onClick={() => setFieldsValue({ node_type: type.value })}>
                          <div className="node-type-icon">
                            <IconComponent />
                          </div>
                          <div className="node-type-label">{type.label}</div>
                          {selectedType === type.value && (
                            <div className="node-type-check">
                              <CheckOutlined />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              }}
            </Form.Item>
          </Form.Item>
          <Form.Item 
            label="接入协议驱动" 
            name="drive_instance_id" 
            rules={[{ required: true, message: '请选择接入协议驱动' }]}
            extra={
              <span style={{ display: 'block', marginTop: '8px' }}>
                您也可以进入
                <Link 
                  onClick={() => navigate('/driver/image')}
                  style={{ marginLeft: '4px', marginRight: '4px' }}
                >
                  驱动市场
                </Link>
                添加新的驱动
              </span>
            }
          >
            <Select placeholder="请选择接入协议驱动" options={protocolList}></Select>
          </Form.Item>
          <Form.Item label="品牌" name="brand" initialValue={'无品牌'}>
            <Input placeholder="请输入"></Input>
          </Form.Item>
          <Form.Item label="产品描述" name="description">
            <TextArea placeholder="请输入产品描述" rows={4}></TextArea>
          </Form.Item>
        </Form>
      </Spin>
    </Drawer>
  );
});

export default AddModal;
